/**
 * ALDER SEARCH — Approach Page
 * Route: /approach
 * Design: "Alder Forest — Organic Precision" — matches existing site exactly
 * Contains: How We Work (four-phase process) + What Sets Us Apart (differentiators) + stats + CTA
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { media } from "@/data/media";

const steps = [
  {
    phase: "Phase 01",
    title: "Mandate Alignment",
    description:
      "We start by getting under the skin of your investment thesis and value creation plan. We ask hard questions, challenge the brief where needed, and make sure we're searching for the right person — not just the person the brief describes — before any outreach begins.",
  },
  {
    phase: "Phase 02",
    title: "Market Mapping",
    description:
      "Our proprietary network of product executives and practitioners spans every major sector, from VC-backed startups to PE-backed scale-ups. We map the full universe of qualified candidates including those not actively seeking a move and assess each against your criteria.",
  },
  {
    phase: "Phase 03",
    title: "Rigorous Assessment",
    description:
      "Every shortlisted candidate undergoes structured competency interviews, reference validation, and a product-specific leadership assessment for VC and PE. We evaluate not just capability, but cultural fit with your sponsor model and the portfolio company's stage.",
  },
  {
    phase: "Phase 04",
    title: "Placement & Onboarding",
    description:
      "We manage the offer process, navigate competing interests, and remain engaged through the first 90 days post-placement. Our retention guarantee reflects our confidence in the quality of every placement we make. If a placed hire leaves within 3 months, we rerun the search at no additional retainer.",
  },
];

const differentiators = [
  {
    title: "VC & PE-Exclusive, Product Only",
    description:
      "Every search we accept is for a VC or PE investor or portfolio company, and every mandate is product. Our network, our methodology, and our judgment are calibrated entirely to your world.",
  },
  {
    title: "One Specialist, Accountable for Every Search",
    description:
      "Ryan has spent 15+ years recruiting product leaders and leads every mandate personally. There is no hand-off to a junior consultant after the pitch. The person who briefs you is the person who runs the search and presents the shortlist.",
  },
  {
    title: "Retained, Always",
    description:
      "We do not conduct contingency searches. Every mandate is exclusive and retained, which means our full attention, our entire network, and our complete methodology are deployed on your behalf from the moment we shake hands.",
  },
  {
    title: "Proprietary Network",
    description:
      "Over 15 years, we have built a proprietary database of 10,000+ product leaders in Ryan's network across every sector, from VC-backed startups to PE-backed scale-ups. The best candidates are rarely on the market — they are in our network.",
  },
];

const statsData = [
  { value: 72, suffix: " hours", label: "To first qualified candidates" },
  { value: 96.3, suffix: "%", label: "Placements still in role after 12 months" },
  { value: 10000, suffix: "+", label: "Product leaders in Ryan's network" },
  { value: 15, suffix: "+", label: "Years recruiting product leaders" },
];

function CountUp({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const duration = 1800;
            const steps = 60;
            const increment = target / steps;
            let current = 0;
            const timer = setInterval(() => {
              current += increment;
              if (current >= target) {
                setCount(target);
                clearInterval(timer);
              } else {
                setCount(target % 1 === 0 ? Math.floor(current) : Number(current.toFixed(1)));
              }
            }, duration / steps);
          }
        });
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {count.toLocaleString("en-GB")}
      {suffix}
    </span>
  );
}

export default function ApproachPage() {
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);
  const diffRef = useRef<(HTMLDivElement | null)[]>([]);
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  // SEO meta tags
  useEffect(() => {
    document.title = "Our Approach | Alder Search — Product Executive Search for VC & PE";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "How Alder Search works: four-phase retained product search methodology for VC and PE. Mandate alignment, market mapping, rigorous assessment, and onboarding support."
      );
    }
    return () => {
      document.title = "Alder Search | Retained Product Executive Search for VC and PE";
    };
  }, []);

  // Scroll reveal
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const allEls = [
      ...stepsRef.current,
      ...diffRef.current,
      ...sectionsRef.current,
    ];
    if (prefersReduced) {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    allEls.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      el.style.transition = `opacity 0.7s ease-out ${i * 80}ms, transform 0.7s ease-out ${i * 80}ms`;
      observer.observe(el);
    });
    const fallback = setTimeout(() => {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    }, 2000);
    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.978 0.006 80)" }}>
      <Navigation />

      {/* ── HERO ── */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{ background: "oklch(0.245 0.065 155)" }}
      >
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `url(${media.abstract.webp})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="container relative z-10 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
            <span
              className="font-ui text-xs tracking-widest uppercase"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.18em",
              }}
            >
              How We Work
            </span>
          </div>
          <h1
            className="font-display mb-6"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(2.4rem, 5vw, 4rem)",
              color: "oklch(0.952 0.012 80)",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
            }}
          >
            A Process Built for
            <br />
            <em style={{ fontStyle: "italic", color: "oklch(0.72 0.09 75)" }}>
              High-Stakes Mandates.
            </em>
          </h1>
          <p
            className="font-body max-w-xl"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 400,
              fontSize: "1.1rem",
              lineHeight: 1.75,
              color: "oklch(0.75 0.02 155)",
            }}
          >
            Every Alder Search and Alder Talent mandate follows the same four-phase methodology.
            Retained. Exclusive. Product-specific, for VC and PE, from the first conversation to the first 90 days post-placement.
          </p>
        </div>
      </section>

      {/* ── HOW WE WORK ── */}
      <section
        className="py-28 relative overflow-hidden"
        style={{ background: "oklch(0.245 0.065 155)" }}
      >
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `url(${media.abstract.webp})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left: Process Steps */}
            <div className="flex flex-col gap-0">
              {steps.map((step, i) => (
                <div
                  key={step.phase}
                  ref={(el) => { stepsRef.current[i] = el; }}
                  className="relative flex gap-6 pb-10 last:pb-0"
                >
                  {i < steps.length - 1 && (
                    <div
                      className="absolute left-5 top-10 bottom-0 w-px"
                      style={{
                        background:
                          "linear-gradient(to bottom, oklch(0.62 0.085 75 / 0.4), transparent)",
                      }}
                    />
                  )}
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{
                        border: "1px solid oklch(0.62 0.085 75 / 0.5)",
                        background: "oklch(0.32 0.07 155)",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontWeight: 400,
                          fontSize: "0.65rem",
                          color: "oklch(0.62 0.085 75)",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 pt-1.5">
                    <div
                      className="font-ui text-xs mb-1.5"
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 400,
                        fontSize: "0.68rem",
                        color: "oklch(0.62 0.085 75)",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      {step.phase}
                    </div>
                    <h3
                      className="font-display mb-2"
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontWeight: 600,
                        fontSize: "1.15rem",
                        color: "oklch(0.952 0.012 80)",
                        lineHeight: 1.3,
                      }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="font-body"
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        fontWeight: 300,
                        fontSize: "0.9rem",
                        lineHeight: 1.8,
                        color: "oklch(0.75 0.02 155)",
                      }}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Image + Pull Quote */}
            <div
              ref={(el) => { sectionsRef.current[0] = el; }}
              className="flex flex-col gap-6 lg:sticky lg:top-28"
            >
              <div
                className="relative overflow-hidden"
                style={{ borderRadius: "4px", aspectRatio: "4/3" }}
              >
                <picture>
                  <source type="image/avif" srcSet={media.approach.avif} />
                  <img
                    src={media.approach.webp}
                    srcSet={media.approach.srcSet}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    alt="Executive discussion in boardroom"
                    width={media.approach.width}
                    height={media.approach.height}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                    style={{ filter: "brightness(0.9) saturate(0.95)" }}
                  />
                </picture>
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, oklch(0.18 0.055 155 / 0.6) 0%, transparent 60%)",
                  }}
                />
              </div>
              <div
                className="p-8 relative"
                style={{
                  background: "oklch(0.32 0.07 155)",
                  borderRadius: "4px",
                  borderLeft: "3px solid oklch(0.62 0.085 75)",
                }}
              >
                <p
                  className="font-body mb-4"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontStyle: "italic",
                    fontWeight: 400,
                    fontSize: "1rem",
                    lineHeight: 1.75,
                    color: "oklch(0.88 0.015 80)",
                  }}
                >
                  Ryan is a delight to work with, very responsive and finely tuned to the needs of both the candidates and the business talent requirements. I&apos;d highly recommend Ryan for senior product hiring!
                </p>
                <div
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 500,
                    fontSize: "0.72rem",
                    color: "oklch(0.62 0.085 75)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  Madhu Nori, Interim Chief Product Officer, Datapharm
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <section className="py-20" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="container">
          <div
            ref={(el) => { sectionsRef.current[1] = el; }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {statsData.map((stat) => (
              <div key={stat.label} className="flex flex-col items-center text-center gap-2">
                <div
                  className="font-display"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 700,
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    color: "oklch(0.72 0.09 75)",
                    lineHeight: 1,
                  }}
                >
                  <CountUp target={stat.value} suffix={stat.suffix} />
                </div>
                <div
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 400,
                    fontSize: "0.72rem",
                    color: "oklch(0.55 0.02 155)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT SETS US APART ── */}
      <section className="py-28" style={{ background: "oklch(0.978 0.006 80)" }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left sticky header */}
            <div
              ref={(el) => { sectionsRef.current[2] = el; }}
              className="lg:sticky lg:top-28"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
                <span
                  className="font-ui text-xs tracking-widest uppercase"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    color: "oklch(0.62 0.085 75)",
                    letterSpacing: "0.18em",
                  }}
                >
                  Why Alder
                </span>
              </div>
              <h2
                className="font-display mb-6"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 700,
                  fontSize: "clamp(2rem, 4vw, 3.2rem)",
                  color: "oklch(0.245 0.065 155)",
                  lineHeight: 1.15,
                  letterSpacing: "-0.015em",
                }}
              >
                The Reasons Sponsors
                <br />
                Come Back.
              </h2>
              <p
                className="font-body"
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontWeight: 400,
                  fontSize: "1rem",
                  lineHeight: 1.8,
                  color: "oklch(0.45 0.015 155)",
                  maxWidth: "380px",
                }}
              >
                Executive search is a relationship business. The firms that consistently deliver
                are those with the deepest networks, the sharpest judgment, and the most relevant
                experience. We have built Alder Search to excel on all three.
              </p>
            </div>

            {/* Right: differentiator list */}
            <div className="flex flex-col gap-0">
              {differentiators.map((item, i) => (
                <div
                  key={item.title}
                  ref={(el) => { diffRef.current[i] = el; }}
                  className="group py-8 border-b"
                  style={{ borderColor: "oklch(0.88 0.018 80)" }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="flex-shrink-0 w-8 h-8 flex items-center justify-center mt-0.5"
                      style={{
                        border: "1px solid oklch(0.62 0.085 75 / 0.4)",
                        borderRadius: "2px",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontWeight: 400,
                          fontSize: "0.6rem",
                          color: "oklch(0.62 0.085 75)",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div>
                      <h3
                        className="font-display mb-2"
                        style={{
                          fontFamily: "'Playfair Display', Georgia, serif",
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          color: "oklch(0.245 0.065 155)",
                          lineHeight: 1.3,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {item.title}
                      </h3>
                      <p
                        className="font-body"
                        style={{
                          fontFamily: "'Source Serif 4', Georgia, serif",
                          fontWeight: 400,
                          fontSize: "0.9rem",
                          lineHeight: 1.8,
                          color: "oklch(0.45 0.015 155)",
                        }}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="py-28 relative overflow-hidden"
        style={{ background: "oklch(0.18 0.055 155)" }}
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, oklch(0.62 0.085 75) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
        <div className="container relative z-10">
          <div
            ref={(el) => { sectionsRef.current[3] = el; }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.18em",
                }}
              >
                Start a Conversation
              </span>
            </div>
            <h2
              className="font-display mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4vw, 3.2rem)",
                color: "oklch(0.952 0.012 80)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              Ready to Engage Alder?
            </h2>
            <p
              className="font-body mb-10"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 300,
                fontSize: "1.05rem",
                lineHeight: 1.8,
                color: "oklch(0.75 0.02 155)",
              }}
            >
              Whether you have an active mandate, are preparing for a transaction, or want to
              explore how Alder Search or Alder Talent can support your portfolio — we welcome
              the conversation.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Link
                href="/#contact"
                className="font-ui text-sm font-600 px-8 py-3.5 transition-all duration-300 inline-block"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  background: "oklch(0.62 0.085 75)",
                  color: "oklch(0.245 0.065 155)",
                  borderRadius: "2px",
                  textDecoration: "none",
                }}
              >
                Engage Alder Search
              </Link>
              <Link
                href="/executive-search"
                className="font-ui text-sm font-500 px-8 py-3.5 transition-all duration-300 inline-block"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontWeight: 500,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  border: "1px solid oklch(0.62 0.085 75 / 0.5)",
                  color: "oklch(0.85 0.015 80)",
                  borderRadius: "2px",
                  textDecoration: "none",
                }}
              >
                Our Practices
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FURTHER READING */}
      <section
        className="py-16"
        style={{ background: "oklch(0.97 0.006 80)", borderTop: "1px solid oklch(0.88 0.01 80)" }}
      >
        <div className="container">
          <div
            className="mb-2"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.62rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "oklch(0.62 0.085 75)",
            }}
          >
            Further Reading
          </div>
          <h2
            className="mb-8"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)",
              lineHeight: 1.2,
              color: "oklch(0.245 0.065 155)",
            }}
          >
            Related Insights
          </h2>
          <Link
            href="/insights/pe-talent-diligence-checklist"
            style={{ textDecoration: "none", display: "block", maxWidth: "560px" }}
            onMouseEnter={(e) => {
              const card = e.currentTarget as HTMLAnchorElement;
              card.style.borderColor = "oklch(0.62 0.085 75)";
            }}
            onMouseLeave={(e) => {
              const card = e.currentTarget as HTMLAnchorElement;
              card.style.borderColor = "oklch(0.88 0.01 80)";
            }}
          >
            <div
              className="p-6"
              style={{
                border: "1px solid oklch(0.88 0.01 80)",
                borderRadius: "3px",
                transition: "border-color 0.2s ease",
              }}
            >
              <div
                className="mb-2"
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "0.6rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "oklch(0.62 0.085 75)",
                }}
              >
                Due Diligence · April 2026
              </div>
              <h3
                className="mb-2"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 600,
                  fontSize: "1.05rem",
                  lineHeight: 1.35,
                  color: "oklch(0.245 0.065 155)",
                }}
              >
                The PE Talent Diligence Checklist: Your Guide to Evaluating Leadership Risk
              </h3>
              <p
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontWeight: 300,
                  fontSize: "0.88rem",
                  lineHeight: 1.65,
                  color: "oklch(0.42 0.03 155)",
                  marginBottom: "1rem",
                }}
              >
                A practical checklist covering management team evaluation, key person risk, compensation alignment, and culture fit -- the four areas standard due diligence misses.
              </p>
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "0.62rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "oklch(0.62 0.085 75)",
                }}
              >
                Read article →
              </span>
            </div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
